import { useCallback, useRef, useEffect } from 'react';
import multiItemAccessoryUpdateCartService from '../../../services/MultiItemAccessories/multiitemAccessoriesUpdateCartService';
import { actionTypes } from './MultiAccessoryProvider';
import { CONTRACT, DATA_POSTPAID, HARDWARE, HARDWAREGROUPNAME, INTERNETFLOW, MOBILERATEPLAN, NONE, RATEPLAN, TABLETS, TV, WATCHES, HARDWARE_CONFIGURATION, HARDWARE_IMAGE_URL, HARDWARE_CATEGORY, UPFRONT, SMARTWATCH, TABLET_CATEGORY, UPDATE } from '../../../utils/constants';
import { countZeros } from '../../../utils/utils';
import { ACCESSORY, ADD, FLOWINTITIATED, MONTHLYFLASHDISCOUNT, REMOVE, TOTALFLASHDISCOUNT, MONTHLY } from '../../../utils/constants';
import { updateCreatecartItemData } from './types';
import { findAllCartItemsByCategory } from '../../../utils/DeviceUtils';
import { useMultiAccessoryContext } from './useMultiAccessoryContext';

/**
 * Custom hook that provides action creators for Multi Accessory Context
 * Reads state directly from MultiAccessoryContext — no need to pass dispatch or state from parent
 * @returns Object containing all action creator functions
 */
export const useMultiAccessoryUpdateCartActions = () => {
  const { store, dispatch } = useMultiAccessoryContext();
  const storeRef = useRef(store);

  useEffect(() => {
    storeRef.current = store;
  }, [store]);

  // Action: Set Update Cart Success
  const setupdateAccessoryCartSuccess = useCallback((payload: any) => {
    dispatch({ type: actionTypes.updateAccessoryCartSuccess, payload });
  }, [dispatch]);
  // Action: Set Update Cart Failure
  const setupdateAccessoryCartFailure = useCallback((payload: any) => {
    dispatch({ type: actionTypes.updateAccessoryCartFailure, payload });
  }, [dispatch]);

  /**
   * Parses the update-cart response as the latest authoritative cart state.
   * Only preserves subscribedInfo and processType from existing cartData if missing.
   */
  const mergeCartData = (responseCartItems: any[]): Record<string, any> => {
    const updatedCartData = findAllCartItemsByCategory(responseCartItems);
    const existingCartData = storeRef.current.cartData;
    
    // Preserve only metadata that's not part of the cart structure itself
    if (!updatedCartData.subscribedInfo && existingCartData?.subscribedInfo) {
      updatedCartData.subscribedInfo = existingCartData.subscribedInfo;
    }
    if (!updatedCartData.processType && existingCartData?.processType) {
      updatedCartData.processType = existingCartData.processType;
    }
    return updatedCartData;
  };

  /**
   * @param redirectUrl => redirection url for checkout page
   */

  const multiItemAccessorycreateCartRequest = useCallback(async (
    updateCreatecartItemData: updateCreatecartItemData,
  ) => {
    try {
      const multiAccessoryState = storeRef.current;
      const { selectedPaymentType , recommendedAccessories , selectedTab, deviceSessionValues} = multiAccessoryState;
      //check for WIN/rateplan flow
      const isSubscriptionFlow = [MOBILERATEPLAN, INTERNETFLOW, TV ]?.includes?.(recommendedAccessories?.[selectedTab]?.hardware?.[0]?.recommendationProcessingType) ? true : (deviceSessionValues?.hardwareVariant ? false : true);
      // eslint-disable
      const updateCartItemList: any = accesoryUpdateCartItemList(multiAccessoryState,updateCreatecartItemData) 


      // win flow when there is change in payment type 
      const updateCartRequest = {
        ...(isSubscriptionFlow && { recommendationItemTerm: selectedPaymentType === CONTRACT ? MONTHLY : NONE }),
        ...(updateCartItemList?.[0]?.items?.length > 0 ? { cartItem: updateCartItemList } : {})
      };

      dispatch({ type: actionTypes.updateAccessoryCartStart, payload: null });
      // Determine usecase: use previousTab only for tab-switch, otherwise use current selectedTab
      const tabForUsecase = updateCreatecartItemData?.previousTab || selectedTab;
      const usecase = (tabForUsecase === TABLETS || tabForUsecase === WATCHES) ? tabForUsecase : undefined;
      // Call the service to make the API request with the prepared requestBody
      const response = updateCartRequest && await multiItemAccessoryUpdateCartService?.getUpdateCartData(
        updateCreatecartItemData?.id,
        updateCartRequest,
        usecase
      );
      if (response) {
        dispatch({ type: actionTypes.updateAccessoryCartSuccess, payload: response });
        dispatch({ type: actionTypes.getCartSuccess, payload: mergeCartData(response?.cartItem) });

        // Re-read latest store after async API call
        const latestState = storeRef.current;
        if (updateCreatecartItemData?.previousTab) {
          // Clear selectedAccessories and removeSelectedCartAccessories for the previousTab
          dispatch({
            type: actionTypes.selectedAccessories,
            payload: { ...latestState.selectedAccessories, [updateCreatecartItemData.previousTab]: {} }
          });
          dispatch({
            type: actionTypes.removeSelectedCartAccessories,
            payload: { ...latestState.removeSelectedCartAccessories, [updateCreatecartItemData.previousTab]: {} }
          });
        } else {
          window.location.href = updateCreatecartItemData?.redirectUrl;
        }
      }
      return response; // Return the response from the API
    } catch (error) {
      dispatch({
        type: actionTypes.updateAccessoryCartFailure,
        payload: { Error: error instanceof Error ? error.message : String(error) }
      });
    }
  }, [dispatch]);

  /**
   * Function to fetch cart data and store in state
   * @param cartId - The cart ID to fetch
   */
  const fetchCartData = useCallback(async (cartId: any) => {
    try {
      dispatch({ type: actionTypes.getCartStart, payload: null });
      
      // Call the service to fetch cart data
      const response = await multiItemAccessoryUpdateCartService?.getCart(cartId);
      
      if (response && !response.error) {
        dispatch({
          type: actionTypes.getCartSuccess,
          payload: findAllCartItemsByCategory(response?.cartItem)
        });
        return response;
      } else {
        throw new Error(response?.error || 'Failed to fetch cart data');
      }
    } catch (error) {
      dispatch({
        type: actionTypes.getCartFailure,
        payload: { Error: error instanceof Error ? error.message : String(error) }
      });
      throw error;
    }
  }, [dispatch]);

  /**
   * Remove a single item from cart via direct API call.
   * Builds the payload from the passed item directly — does not rely on store state.
   * Does not clear selectedAccessories or removeSelectedCartAccessories after success.
   */
  const removeItemFromCart = useCallback(async (cartId: any, item: any) => {
    try {
      const tabKey = item?.tabKey;
      const multiAccessoryState = storeRef.current;
      const { selectedPaymentType, recommendedAccessories, cartData, selectedAccessories, deviceSessionValues } = multiAccessoryState;
      const isSubscriptionFlow = [MOBILERATEPLAN, INTERNETFLOW, TV]?.includes?.(
        recommendedAccessories?.[tabKey]?.hardware?.[0]?.recommendationProcessingType
      ) ? true : (deviceSessionValues?.hardwareVariant ? false : true);
      const isAlreadyInCart = Object.keys(cartData?.[tabKey] || {})?.length > 0 && Object.keys(selectedAccessories?.[tabKey] || {})?.length > 0;

      // Build the remove item
      const items: any = [];
      if ((tabKey === WATCHES || tabKey === TABLETS) && !isAlreadyInCart) {
        items.push({
          itemType: HARDWARE,
          action: REMOVE,
          id: item?.recomApiResponse?.id,
        });
      } else if (tabKey === ACCESSORY) {
        items.push({
          action: REMOVE,
          id: item?.recomApiResponse?.id,
        });
      }

      if (items.length === 0) return;
      const promotionId = recommendedAccessories?.[tabKey]?.ratePlan?.promotionInfo?.id ?? 'defaultMBBExtraSimBundle'; // Todo with cart response
      const updateCartRequest = {
        ...(isSubscriptionFlow && { recommendationItemTerm: selectedPaymentType === CONTRACT ? MONTHLY : NONE }),
        cartItem: [{
          itemType: tabKey === ACCESSORY ? ACCESSORY : DATA_POSTPAID,
          promotionId: promotionId,
          items: items,
        }],
      };

      dispatch({ type: actionTypes.updateAccessoryCartStart, payload: null });

      const usecase = (tabKey === TABLETS || tabKey === WATCHES) ? tabKey : undefined;
      const response = await multiItemAccessoryUpdateCartService?.getUpdateCartData(
        cartId,
        updateCartRequest,
        usecase
      );

      if (response) {
        dispatch({ type: actionTypes.updateAccessoryCartSuccess, payload: response });
        dispatch({ type: actionTypes.getCartSuccess, payload: mergeCartData(response?.cartItem) });
      }
      return response;
    } catch (error) {
      dispatch({
        type: actionTypes.updateAccessoryCartFailure,
        payload: { Error: error instanceof Error ? error.message : String(error) },
      });
    }
  }, [dispatch]);

  // Return all action creators
  return {
    setupdateAccessoryCartSuccess,
    setupdateAccessoryCartFailure,
    multiItemAccessorycreateCartRequest,
    fetchCartData,
    removeItemFromCart,
  };
};

// adding the necessary cart item for cart payload (changed request payload for WIN...to be considered for future scope)
const accesoryUpdateCartItemList = (multiAccessoryState: any, updateCreatecartItemData: updateCreatecartItemData) => {
  const { selectedAccessories, deviceSessionValues, removeSelectedCartAccessories, selectedPaymentType, recommendedAccessories, cartData } = multiAccessoryState;
  const isDeviceRenewal = cartData?.processType === 'DEVICE_RENEWAL' || cartData?.processType === 'DEVICE_RP_RENEWAL';
  const cartItem: any = [];
 
  // Determine which tabs to process based on previousTab
  const tabsToProcess = updateCreatecartItemData?.previousTab
    ? [updateCreatecartItemData.previousTab]
    : Object.keys(recommendedAccessories || {});

  tabsToProcess?.forEach((tabKey) => {
    const items: any = [];
    // Check if this item is already in the cart
    const isAlreadyInCart = Object.keys(cartData?.[tabKey] || {})?.length > 0 &&  Object.keys(selectedAccessories?.[tabKey] || {})?.length > 0;
       
    // Process selected accessories
    Object.keys(selectedAccessories?.[tabKey] || {})?.forEach((key) => {
      const selectedAccessory = selectedAccessories?.[tabKey]?.[key];
      const discounts: any = {
        monthlyFlashDiscountPrice: selectedAccessory?.recomApiResponse?.recurringPrice?.marketMonthlyInstallment
          ? countZeros(
            (
              parseFloat(selectedAccessory?.recomApiResponse?.recurringPrice?.marketMonthlyInstallment) -
              parseFloat(selectedAccessory?.recomApiResponse?.recurringPrice?.monthlyInstallment)
            )
          )
          : countZeros(0),
        totalFlashDiscountPrice: selectedAccessory?.recomApiResponse?.oneTimePrice?.market
          ? countZeros(parseFloat(selectedAccessory?.recomApiResponse?.oneTimePrice?.market) - parseFloat(selectedAccessory?.recomApiResponse?.oneTimePrice?.base))
          : countZeros(0)
      };
      
      // For WATCHES and TABLETS, create a different structure
      if (tabKey === WATCHES || tabKey === TABLETS) {
        const cartAction = isAlreadyInCart ? UPDATE : ADD;
        const cartItemNote: any = [];

        cartItemNote.push(
          {
            author: FLOWINTITIATED,
            text: HARDWARE
          },
          {
            author: HARDWARE_CONFIGURATION,
            text: selectedAccessory?.recomApiResponse?.variantCategory?.color || ''
          },
          {
            author: HARDWARE_IMAGE_URL,
            text: selectedAccessory?.imageData?.image || ''
          },
          {
            author: HARDWAREGROUPNAME,
            text: selectedAccessory?.recomApiResponse?.baseProducts || selectedAccessory?.recomApiResponse?.name
          },
          {
            author: HARDWARE_CATEGORY,
            text: tabKey === WATCHES ? SMARTWATCH : TABLET_CATEGORY
          },
          {
            author: 'isWorldPassBundle', // Todo needs ot be remove once new flag is adapted
            text: 'true'
          },
          {
            author: 'enableHardwareBundling', // passing generic flag for handling hardware bundling (both world pass and device bundling) in summary
            text: 'true'
          },
        );

        // Pass upfront value in notes
        if (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) {
          cartItemNote.push({
            author: UPFRONT,
            text: '1.0'
          });
        }

        // For watches/tablets, we need to add RATEPLAN first, then HARDWARE
        // Check if rateplan info exists in the recommendation
        const ratePlanId = recommendedAccessories?.[tabKey]?.ratePlan?.id;

        // Add RATEPLAN item only for new additions (not updates)
        if (!isAlreadyInCart && ratePlanId) {
          const ratePlanItem: any = {
            itemType: RATEPLAN,
            action: ADD,
            id: ratePlanId,
          };
          // For DEVICE_RENEWAL orders, include only id and installedPlan from subscribedInfo
          if (isDeviceRenewal && cartData?.subscribedInfo) {
            ratePlanItem.subscribedInfo = {
              id: cartData.subscribedInfo?.id,
              installedPlan: cartData.subscribedInfo?.installedPlan,
            };
          }
          items.push(ratePlanItem);
        }

        // Add HARDWARE item with UPDATE action if already in cart
        items.push({
          itemType: HARDWARE,
          action: cartAction,
          id: selectedAccessory?.recomApiResponse?.id,
          contractTerm: (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) ? 24 : 0,
          note: cartItemNote
        });
      } else {
        // ACCESSORY flow
        const cartItemNote: any = [];

        cartItemNote.push(
          {
            author: FLOWINTITIATED,
            text: HARDWARE
          },
          {
            author: MONTHLYFLASHDISCOUNT,
            text: discounts?.monthlyFlashDiscountPrice
          },
          {
            author: TOTALFLASHDISCOUNT,
            text: discounts?.totalFlashDiscountPrice
          },
          {
            author: HARDWAREGROUPNAME,
            text: selectedAccessory?.recomApiResponse?.baseProducts
          }
        );

        // pass upfront value in notes incase of contract payment
        if (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) {
          cartItemNote.push({
            author: UPFRONT,
            text: 1.0
          });
        }

        items.push({
          action: ADD,
          id: selectedAccessory?.recomApiResponse?.id,
          contractTerm: (deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT || selectedPaymentType === CONTRACT) ? 24 : 0,
          note: cartItemNote
        });
      }
    });

    // Process removed accessories
    Object.keys(removeSelectedCartAccessories?.[tabKey] || {})?.forEach((key) => {
      if ((tabKey === WATCHES || tabKey === TABLETS) && !isAlreadyInCart) {
        // For watches/tablets, remove HARDWARE
        items.push({
          itemType: HARDWARE,
          action: REMOVE,
          id: removeSelectedCartAccessories?.[tabKey]?.[key]?.recomApiResponse?.id,
        });
      } else if(tabKey === ACCESSORY){
        items.push({
          action: REMOVE,
          id: removeSelectedCartAccessories?.[tabKey]?.[key]?.recomApiResponse?.id,
        });
      }
    });
    const promotionId = recommendedAccessories?.[tabKey]?.ratePlan?.promotionInfo?.id;
    
    // Only add cartItem for this tab if there are items
    if (items.length > 0) {
      cartItem.push({
        itemType: tabKey === ACCESSORY ? ACCESSORY : DATA_POSTPAID,
        promotionId: promotionId,
        items: items
      });
    }
  });

  return cartItem;
};
